import React, { ReactNode, isValidElement, cloneElement } from 'react';
import { SiteLocale, TranslationType, InterpolateArgs } from '../types';

const interpolate = (s: string, args?: InterpolateArgs): ReactNode => {
  if (!args || !Object.keys(args).length) {
    return s;
  }

  // Check if any of the args are React elements
  const hasReactElements = Object.values(args).some((value) =>
    isValidElement(value),
  );

  // If no React elements, return string interpolation
  if (!hasReactElements) {
    return Object.entries(args).reduce(
      (acc, [key, value]) =>
        `${acc.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), `${value}`)}`,
      s,
    );
  }

  // Handle React element interpolation
  const parts: ReactNode[] = [];
  let remaining = s;
  let keyIndex = 0;

  Object.entries(args).forEach(([key, value]) => {
    const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
    const matches = [...remaining.matchAll(regex)];

    if (matches.length > 0) {
      matches.forEach((match) => {
        const beforeMatch = remaining.substring(0, match.index);
        if (beforeMatch) {
          parts.push(beforeMatch);
        }

        if (isValidElement(value)) {
          parts.push(cloneElement(value, { key: `interpolated-${keyIndex}` }));
          keyIndex += 1;
        } else {
          parts.push(String(value));
        }

        remaining = remaining.substring((match.index || 0) + match[0].length);
      });
    }
  });

  if (remaining) {
    parts.push(remaining);
  }

  return parts.length === 1 && typeof parts[0] === 'string'
    ? parts[0]
    : React.createElement(React.Fragment, {}, ...parts);
};

export function getTranslation({
  locale,
  defaultLocale,
  key,
  translations,
  args,
}: {
  locale: SiteLocale;
  defaultLocale?: SiteLocale;
  key: string;
  translations: TranslationType;
  args?: InterpolateArgs;
}): ReactNode {
  if (!locale && !defaultLocale) {
    return key;
  }

  const translation = translations?.[key];
  if (translation) {
    return interpolate(translation, args);
  }
  if (args?.fallback) {
    const { fallback, ...remainingArgs } = args;

    const fallbackTranslation = translations?.[fallback];
    if (fallbackTranslation) {
      return interpolate(fallbackTranslation, remainingArgs);
    }
  }

  if (
    typeof translation === 'undefined' &&
    process.env.NODE_ENV === 'development'
  ) {
    (async () => {
      const { addTranslationKey } = await import('./translations.utils');
      addTranslationKey(key, locale);
    })();
  }

  return key;
}

export default { getTranslation };
